import { useEffect, useRef, useState } from "react";

import { ensureCsrfToken, getLegacyUrl } from "./api";

import { PDFDocument } from "pdf-lib";
import Swal from "sweetalert2";

function formatTeamLabel(teamCode) {
  return teamCode || "Not selected";
}

export default function UploaderUploadInvoiceView({ selectedTeam, availableTeams = [] }) {
  const inputRef = useRef(null);
  const [team, setTeam] = useState(selectedTeam || availableTeams[0] || "");
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (selectedTeam && availableTeams.includes(selectedTeam)) {
      setTeam(selectedTeam);
      return;
    }

    if (!team && availableTeams.length === 1) {
      setTeam(availableTeams[0]);
    }
  }, [availableTeams, selectedTeam, team]);

  function updateFiles(nextFiles) {
    setFiles(Array.from(nextFiles || []));
    setError("");
  }

  function handleInputChange(event) {
    updateFiles(event.target.files);
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragActive(false);
    updateFiles(event.dataTransfer.files);
  }

  async function isPasswordProtectedPdf(file) {
    try {
      const bytes = await file.arrayBuffer();
      await PDFDocument.load(bytes);
      return false;
    } catch {
      return true;
    }
  }

  async function checkInvoiceUploadRequirements() {
    const response = await fetch("/api/get-invoice-upload-requirements/");

    if (!response.ok) {
      throw new Error("Failed to check invoice upload requirements.");
    }

    return await response.json();
  }

  function showUploadRequirementsPopup(requirements) {
    const rows = Object.entries(requirements).map(
      ([name, requirement]) => {
        const isConfiguration = name === "configuration";

        const mappingApplicable =
          !["configuration"].includes(name);

        const mappingStatus = mappingApplicable
          ? requirement.mapping
          : null;

        const dataStatus = requirement.data;

        const hasIssue =
          (mappingStatus === false) ||
          (dataStatus === false);

        return `
          <tr>
            <td style="
              text-align: left;
              font-weight: 500;
              padding: 10px;
              border-bottom: 1px solid #eee;
            ">
              ${name}
            </td>

            <td style="
              text-align: center;
              padding: 10px;
              border-bottom: 1px solid #eee;
            ">
              ${
                mappingStatus === null
                  ? '<span style="color:#999;">—</span>'
                  : mappingStatus
                    ? '<span style="color:#16a34a; font-size:20px;">✓</span>'
                    : '<span style="color:#dc2626; font-size:20px;">✗</span>'
              }
            </td>

            <td style="
              text-align: center;
              padding: 10px;
              border-bottom: 1px solid #eee;
            ">
              ${
                dataStatus
                  ? '<span style="color:#16a34a; font-size:20px;">✓</span>'
                  : '<span style="color:#dc2626; font-size:20px;">✗</span>'
              }
            </td>

            <td style="
              text-align: left;
              padding: 10px;
              border-bottom: 1px solid #eee;
              color: ${hasIssue ? "#dc2626" : "#666"};
              font-size: 13px;
            ">
              ${requirement.action || ""}
            </td>
          </tr>
        `;
      }
    ).join("");

    return Swal.fire({
      title: "Invoice Upload Requirements",
      html: `
        <div style="
          font-size: 14px;
          color: #555;
          margin-bottom: 15px;
        ">
          Some configurations or required data are missing.
          Please review the details below before continuing.
        </div>

        <div style="
          width: 100%;
          overflow-x: auto;
        ">
          <table style="
            width: 100%;
            border-collapse: collapse;
            font-size: 13px;
          ">
            <thead>
              <tr style="
                background: #f5f6f8;
              ">
                <th style="
                  text-align: left;
                  padding: 10px;
                ">
                  Requirement
                </th>

                <th style="
                  text-align: center;
                  padding: 10px;
                ">
                  Mapping
                </th>

                <th style="
                  text-align: center;
                  padding: 10px;
                ">
                  Data
                </th>

                <th style="
                  text-align: left;
                  padding: 10px;
                ">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              ${rows}
            </tbody>
          </table>
        </div>

        <div style="
          margin-top: 18px;
          padding: 12px;
          background: #fff8e1;
          border-radius: 6px;
          font-size: 13px;
          text-align: left;
        ">
          <strong>Warning:</strong>
          Some required configuration or data is missing.
          You can continue uploading, but this may affect invoice processing.
        </div>
      `,

      icon: "warning",

      width: "850px",

      showCancelButton: true,

      confirmButtonText: "Continue Upload",
      cancelButtonText: "Cancel",

      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#6c757d",

      reverseButtons: true,

      allowOutsideClick: false,
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setStatus("");
    setError("");

    if (!team) {
      setError("Please select a team before uploading invoices.");
      return;
    }

    if (!files.length) {
      setError("Please choose at least one invoice file to upload.");
      return;
    }

    // ---------------------------------------------------------
    // CHECK CONFIGURATION / MASTER DATA BEFORE UPLOAD
    // ---------------------------------------------------------

    try {
      const requirements = await checkInvoiceUploadRequirements();

      const hasMissingRequirements = Object.values(requirements).some(
        (item) =>
          item.mapping === false ||
          item.data === false
      );

      if (hasMissingRequirements) {
        const result =
          await showUploadRequirementsPopup(requirements);

        // User clicked Cancel
        if (!result.isConfirmed) {
          return;
        }

        // User clicked Continue Upload
        // Continue below
      }

    } catch (error) {
      console.error(
        "Error checking invoice upload requirements:",
        error
      );

      setError(
        "Unable to verify invoice upload requirements. Please try again."
      );

      return;
    }

    setSubmitting(true);

    try {
      const validFiles = [];
      const protectedFiles = [];
      const oversizedFiles = [];

      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20 MB
      const ALLOWED_EXTENSIONS = new Set([
        "pdf",
        "jpg",
        "jpeg",
        "png",
        "zip",
        "7z",
        "rar",
      ]);
      const invalidExtensionFiles = [];

      for (const file of files) {

        // Extension validation
        const extension = file.name.split(".").pop()?.toLowerCase();

        if (!extension || !ALLOWED_EXTENSIONS.has(extension)) {
          invalidExtensionFiles.push(file.name);
          continue;
        }

        // File size validation
        if (file.size > MAX_FILE_SIZE) {
          oversizedFiles.push({
            name: file.name,
            size: (file.size / (1024 * 1024)).toFixed(2),
          });
          continue;
        }

        // Password protected PDF validation
        if (file.name.toLowerCase().endsWith(".pdf")) {
          const isProtected = await isPasswordProtectedPdf(file);

          if (isProtected) {
            protectedFiles.push(file.name);
            continue;
          }
        }

        validFiles.push(file);
      }

      // Nothing left to upload
      if (!validFiles.length) {
        await Swal.fire({
          icon: "error",
          title: "Upload Cancelled",
          html: `
            <p>No files could be uploaded.</p>

            ${
              protectedFiles.length
                ? `
                  <p>
                    <strong>${protectedFiles.length}</strong>
                    password-protected PDF(s) were skipped.
                  </p>
                `
                : ""
            }

            ${
              oversizedFiles.length
                ? `
                  <p>
                    <strong>${oversizedFiles.length}</strong>
                    file(s) exceeded the 20 MB limit.
                  </p>
                `
                : ""
            }
          `,
          confirmButtonText: "OK",
        });

        return;
      }

      const csrfToken = await ensureCsrfToken();

      const formData = new FormData();
      formData.append("team", team);

      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch("/api/upload-invoice/", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      const payload = await response.json();

      if (!response.ok || payload.success !== true) {
        throw new Error(
          payload.message ||
          `Upload failed. Backend responded with ${response.status}.`
        );
      }

      const uploadedCount =
        payload.uploaded_count ??
        payload.data?.length ??
        validFiles.length;

      const backendSkippedFiles =
        payload.skipped_files || [];

      const backendSkippedCount =
        payload.skipped_count || 0;

      let html = `
        <p>
          <strong>${uploadedCount}</strong>
          invoice(s) have been uploaded successfully and queued for processing.
        </p>
      `;

      // Frontend password protected files
      if (protectedFiles.length) {
        html += `
          <hr>
          <p>
            <strong>${protectedFiles.length}</strong>
            password-protected PDF(s) were skipped before upload.
          </p>

          <div style="text-align:left;">
            <strong>Skipped PDFs:</strong>
            <ul>
              ${protectedFiles
                .map((name) => `<li>${name}</li>`)
                .join("")}
            </ul>
          </div>
        `;
      }

      // Frontend oversized files
      if (oversizedFiles.length) {
        html += `
          <hr>
          <p>
            <strong>${oversizedFiles.length}</strong>
            file(s) exceeded the 20 MB limit and were skipped.
          </p>

          <div style="text-align:left;">
            <strong>Oversized Files:</strong>
            <ul>
              ${oversizedFiles
                .map(
                  (file) =>
                    `<li>${file.name} (${file.size} MB)</li>`
                )
                .join("")}
            </ul>
          </div>
        `;
      }

      // Backend password protected files
      if (backendSkippedCount > 0) {
        html += `
          <hr>
          <p>
            <strong>${backendSkippedCount}</strong>
            file(s) were rejected by the server.
          </p>

          <div style="text-align:left;">
            <strong>Server Rejections:</strong>
            <ul>
              ${backendSkippedFiles
                .map(
                  (file) =>
                    `<li>${file.fileName}</li>`
                )
                .join("")}
            </ul>
          </div>
        `;
      }
      // Frontend invalid extension files
      if (invalidExtensionFiles.length) {
        html += `
          <hr>
          <p>
            <strong>${invalidExtensionFiles.length}</strong>
            file(s) had unsupported file types and were skipped.
          </p>

          <div style="text-align:left;">
            <strong>Unsupported Files:</strong>
            <ul>
              ${invalidExtensionFiles
                .map((name) => `<li>${name}</li>`)
                .join("")}
            </ul>
          </div>
        `;
      }

      const hasWarnings =
        protectedFiles.length > 0 ||
        oversizedFiles.length > 0 ||
        invalidExtensionFiles.length > 0 ||
        backendSkippedCount > 0;

      await Swal.fire({
        icon: hasWarnings ? "warning" : "success",
        title: hasWarnings
          ? "Upload Completed with Warnings"
          : "Upload Successful",
        html,
        width: "750px",
        confirmButtonText: "OK",
      });

      setFiles([]);

      if (inputRef.current) {
        inputRef.current.value = "";
      }

    } catch (requestError) {
      await Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text:
          requestError.message ||
          "Unable to upload invoices.",
        confirmButtonText: "OK",
      });

      setError(
        requestError.message ||
        "Unable to upload invoices."
      );
    } finally {
      setSubmitting(false);
    }
  }

  function openLegacyUploadPage() {
    window.localStorage.setItem("activeSection", "upload_invoice");
    window.open(getLegacyUrl("/uploader_dashboard/"), "_blank", "noreferrer");
  }

  return (
    <section className="uploader-view">
      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="uploader-layout uploader-layout--stacked">
        <form className="workspace-panel uploader-form" onSubmit={handleSubmit}>
          <div className="uploader-form__header">
            <div>
              <p className="eyebrow">Invoice Intake</p>
              <h2>Upload queue</h2>
            </div>

            <button className="ghost-button" type="button" onClick={openLegacyUploadPage}>
              Open Legacy Upload View
            </button>
          </div>

          <label className="config-field" htmlFor="uploader-team">
            <span>Select Team</span>
            <select
              id="uploader-team"
              value={team}
              disabled={submitting || availableTeams.length <= 1}
              onChange={(event) => setTeam(event.target.value)}
            >
              {availableTeams.length > 1 ? <option value="">Please Select</option> : null}
              {availableTeams.map((teamCode) => (
                <option key={teamCode} value={teamCode}>
                  {formatTeamLabel(teamCode)}
                </option>
              ))}
            </select>
          </label>

          <div
            className={`uploader-dropzone${dragActive ? " is-active" : ""}`}
            role="button"
            tabIndex={0}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragActive(false);
            }}
            onDrop={handleDrop}
          >
            <input
              ref={inputRef}
              className="uploader-dropzone__input"
              type="file"
              name="files"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.zip,.7z,.rar"
              disabled={submitting || !team}
              onChange={handleInputChange}
            />
            <p className="uploader-dropzone__title">Click to select or drag and drop invoice files</p>
            <p className="config-note">Accepted formats: PDF, JPG, JPEG, PNG, ZIP(containing pdf,jpg,png)</p>
            <p className="uploader-dropzone__title">Recommended quality should be atleast 150 DPI for OCR accuracy</p>
            {!team ? <p className="uploader-dropzone__hint">Select a team to enable file selection.</p> : null}
          </div>

          {files.length ? (
            <div className="uploader-file-list">
              {files.map((file) => (
                <article key={`${file.name}-${file.lastModified}`} className="uploader-file-item">
                  <strong>{file.name}</strong>
                  <span>{Math.max(1, Math.round(file.size / 1024))} KB</span>
                </article>
              ))}
            </div>
          ) : (
            <p className="empty-state">No files selected yet.</p>
          )}

          <button className="primary-button uploader-submit" type="submit" disabled={submitting}>
            {submitting ? "Uploading..." : "Upload Files"}
          </button>
        </form>
      </div>
    </section>
  );
}
